import clsx from 'clsx'
import type {
  ChangeEvent,
  ChangeEventHandler,
  PropsWithChildren,
  Ref,
} from 'react'
import React, { useState } from 'react'
import RadioActive from '../icons/RadioActive'
import RadioInactive from '../icons/RadioInactive'
import { useRadio } from './RadioContext'
import type { RadioInputColor, RadioInputSize } from './RadioContext'

interface RadioInputProps {
  /**
   * Accessible label for the radio input
   */
  'aria-label'?: string
  /**
   * The color scheme of the radio input
   * @default 'primary'
   */
  color?: RadioInputColor
  /**
   * If true, the radio input is disabled
   * @default false
   */
  disabled?: boolean
  /**
   * Reference to the underlying input element
   */
  inputRef?: Ref<HTMLInputElement>
  /**
   * The name attribute for the radio input
   */
  name?: string
  /**
   * Callback fired when the input value changes
   */
  onChange?: ChangeEventHandler<HTMLInputElement>
  /**
   * The size of the radio input
   * @default 'small'
   */
  size?: RadioInputSize
  /**
   * The value of the radio input
   */
  value: string | number
}

type RadioInputBackgroundColorType = 'default' | 'focused'

type RadioInputForegroundColorType = 'active' | 'disabled' | 'inactive'

type RadioInputColors = {
  [key in RadioInputColor]: {
    background: { [key in RadioInputBackgroundColorType]: string }
    foreground: { [key in RadioInputForegroundColorType]: string }
  }
}

type RadioInputSizes = {
  [key in RadioInputSize]: string
}

const colors: RadioInputColors = {
  primary: {
    background: {
      default: 'active:bg-primary-20 hover:bg-primary-10',
      focused: 'bg-primary-10',
    },
    foreground: {
      active: 'text-primary-main',
      disabled: 'text-black-30 bg-transparent',
      inactive: 'text-black-60',
    },
  },
  secondary: {
    background: {
      default: 'active:bg-secondary-20 hover:bg-secondary-10',
      focused: 'bg-secondary-10',
    },
    foreground: {
      active: 'text-secondary-main',
      disabled: 'text-black-30 bg-transparent',
      inactive: 'text-black-60',
    },
  },
}

const sizes: RadioInputSizes = {
  medium: 'w-10 h-10 p-2',
  small: 'w-9 h-9 p-2',
}

function Input({
  'aria-label': label,
  children = null,
  color: passedColor = 'primary',
  disabled: passedDisabled = false,
  inputRef = null,
  name: passedName,
  onChange,
  size: passedSize = 'small',
  value,
}: PropsWithChildren<RadioInputProps>): JSX.Element {
  const [
    {
      color: defaultColor,
      disabled: defaultDisabled,
      name,
      size: defaultSize,
      value: checkedValue,
    },
    setCheckedValue,
  ] = useRadio()
  const [focused, setFocused] = useState(() => false)

  const color = defaultColor || passedColor
  const disabled =
    defaultDisabled !== undefined ? defaultDisabled : passedDisabled
  const size = defaultSize || passedSize
  const checked = checkedValue === value

  const handleBlur = () => {
    setFocused(false)
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.currentTarget.checked) {
      setCheckedValue(value)
    }

    if (onChange) {
      onChange(event)
    }
  }

  const handleFocus = () => {
    setFocused(true)
  }

  return (
    <label
      className={clsx(
        'radio font-sans inline-flex items-center group',
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
      )}
    >
      <span
        className={clsx(
          'rounded-full focus:outline-none relative flex',
          disabled ? null : colors[color].background.default,
          focused ? colors[color].background.focused : null,
          sizes[size],
        )}
      >
        <input
          aria-label={label}
          className={clsx(
            'absolute inset-0 w-full h-full p-0 m-0 opacity-0',
            disabled ? 'cursor-not-allowed' : 'cursor-pointer',
          )}
          defaultChecked={checked}
          disabled={disabled}
          name={passedName || name}
          onBlur={handleBlur}
          onChange={handleChange}
          onFocus={handleFocus}
          ref={inputRef}
          type="radio"
          value={value}
        />
        <span
          className={clsx(
            'status-active rounded-full w-full h-full',
            disabled
              ? colors[color].foreground.disabled
              : colors[color].foreground.active,
          )}
        >
          <RadioActive />
        </span>
        <span
          className={clsx(
            'status-inactive rounded-full w-full h-full',
            disabled
              ? colors[color].foreground.disabled
              : colors[color].foreground.inactive,
          )}
        >
          <RadioInactive />
        </span>
      </span>
      {children ? (
        <p
          className={clsx(
            'text-sm font-normal  leading-5 select-none',
            disabled ? 'text-black-45' : 'text-black-90',
          )}
        >
          {children}
        </p>
      ) : null}
    </label>
  )
}

export default Input
